import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Feather, HeartHandshake, Loader2, CheckCircle2, Clock } from "lucide-react";

export default function DonationThanks() {
  const [params] = useSearchParams();
  const donationId = params.get("donation_id");
  const [state, setState] = useState({ status: "checking", amount_pence: 0 });

  useEffect(() => {
    if (!donationId) { setState({ status: "unknown" }); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/donations/status/${donationId}`);
        if (data.status === "COMPLETED" || data.status === "CANCELED") { setState(data); return; }
        setState({ ...data, status: "pending" });
      } catch { /* ignore */ }
      if (tries < 6) timer = setTimeout(poll, 2000);
      else setState((s) => ({ ...s, status: s.status === "checking" ? "pending" : s.status }));
    };
    poll();
    return () => clearTimeout(timer);
  }, [donationId]);

  const done = state.status === "COMPLETED";
  const pounds = ((state.amount_pence || 0) / 100).toFixed(2);

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-5">
      <div className="max-w-md w-full text-center" data-testid="donation-thanks">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-primary grid place-items-center"><Feather className="w-5 h-5 text-primary-foreground" /></div>
          <span className="font-display text-2xl font-bold">CodexStudio</span>
        </Link>

        <div className="rounded-3xl border border-border bg-card p-10">
          {state.status === "checking" ? (
            <><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold">Confirming your donation…</h1></>
          ) : done ? (
            <>
              <div className="w-16 h-16 rounded-full bg-primary/15 grid place-items-center mx-auto mb-5">
                <HeartHandshake className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-display text-3xl font-bold" data-testid="donation-success">Thank you! 💛</h1>
              <p className="text-muted-foreground mt-3">
                Your donation of <span className="font-semibold text-foreground">£{pounds}</span> means the world.
                It genuinely helps keep CodexStudio going.
              </p>
            </>
          ) : state.status === "CANCELED" ? (
            <>
              <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold">Payment cancelled</h1>
              <p className="text-muted-foreground mt-3">No charge was made. You're welcome to try again anytime.</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold">Thanks for your support!</h1>
              <p className="text-muted-foreground mt-3">
                If you completed the payment, it may take a moment to confirm. You can safely close this page.
              </p>
            </>
          )}
          <Button asChild className="mt-8"><Link to="/">Back to CodexStudio</Link></Button>
        </div>
      </div>
    </div>
  );
}
